import { Button, Chip } from '@mui/material';
import React, { Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { payoutManualApi } from 'api';
import {
  CopyTextId,
  DataGrid,
  DataGridColumnDefinition,
  Dialog,
} from 'components';
import { OrdersTab, QueryKey } from 'enums';
import { usePayoutOrdersContext, useQueryFilters, useQueryTab } from 'hooks';
import { TranslationNamespace } from 'i18n';
import {
  PayoutManualBatchItem,
  PayoutManualBatchStatus,
  PayoutOrderFilters,
} from 'types';
import { formatUtils, orderUtils } from 'utils';

import { useRetryableManualOrders } from './use-retryable-manual-orders.hook';

type Props = {
  batchId: string | null;
  onClose: () => void;
};

export const ManualBatchDetailsDialog: React.FC<Props> = ({
  batchId,
  onClose,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders.manual',
  });

  const queryResult = useQuery(
    [QueryKey.PayoutManualBatch, batchId],
    () => payoutManualApi.getBatch(batchId as string),
    { enabled: !!batchId },
  );

  const columns: DataGridColumnDefinition<PayoutManualBatchItem>[] = useMemo(
    () => [
      {
        header: t('result_dialog.columns.order'),
        valueGetter: (item) => <CopyTextId id={item.orderId} />,
      },
      {
        header: t('result_dialog.columns.result'),
        valueGetter: (item) => (
          <Chip
            size="small"
            variant="outlined"
            color={orderUtils.getManualBatchResultColor(item.result)}
            label={orderUtils.getManualBatchResultLabel(
              item.result,
              queryResult.data?.operation,
            )}
          />
        ),
      },
      {
        header: t('batches.columns.status_change'),
        valueGetter: (item) =>
          `${orderUtils.getManualBatchStatusLabel(
            item.previousStatus,
            item.previousStatusDetails,
          )} → ${orderUtils.getManualBatchStatusLabel(
            item.resultStatus,
            item.resultStatusDetails,
          )}`,
      },
      {
        header: t('result_dialog.columns.reason'),
        valueGetter: (item) => orderUtils.getManualBatchItemReason(item) || '—',
      },
      {
        header: t('batches.columns.provider_request_id'),
        valueGetter: (item) =>
          item.providerRequestId ? (
            <CopyTextId id={item.providerRequestId} />
          ) : (
            '—'
          ),
      },
    ],
    [t, queryResult.data?.operation],
  );

  const { setManualSelectedIds } = usePayoutOrdersContext();
  const { setFilters } = useQueryFilters<Partial<PayoutOrderFilters>>();
  const { setTab } = useQueryTab<OrdersTab>();

  const {
    filteredRetryableIds,
    hasNoRetryableOrders,
    isChecking,
    getRetryableIds,
  } = useRetryableManualOrders(
    queryResult.data?.items,
    !!batchId &&
      queryResult.data?.status !== PayoutManualBatchStatus.InProgress,
  );

  const handleClose = useCallback(() => onClose(), [onClose]);

  // Hands the unprocessed orders of this operation back to the queue: selects them, narrows the
  // queue to exactly them and grows the page so they all fit — otherwise part of the selection
  // sits on pages the operator never looks at.
  const handleRetryFailed = useCallback(async () => {
    const filteredAvailableIds = await getRetryableIds();
    if (!filteredAvailableIds.length) {
      return;
    }
    setManualSelectedIds?.(filteredAvailableIds);
    setFilters({
      orderIdsConcatenated: filteredAvailableIds.join(','),
    });
    onClose();
    setTab(OrdersTab.ManualQueue);
  }, [getRetryableIds, onClose, setFilters, setManualSelectedIds, setTab]);

  const title = useMemo(() => {
    if (!queryResult.data) {
      return t('batches.details_title');
    }
    return `${t('batches.details_title')} · ${formatUtils.formatDate(
      queryResult.data.createdAt,
    )}`;
  }, [queryResult.data, t]);

  return (
    <Dialog
      open={!!batchId}
      title={title}
      hideCancel
      maxWidth="lg"
      onClose={handleClose}
    >
      <Fragment>
        <DataGrid columns={columns} data={queryResult.data?.items || []} />
        {hasNoRetryableOrders && (
          <div>{t('result_dialog.no_retryable_orders')}</div>
        )}
        {!!filteredRetryableIds.length && (
          <div className="tw-mt-4">
            <Button
              variant="outlined"
              disabled={isChecking}
              onClick={handleRetryFailed}
            >
              {t('result_dialog.retry_failed', {
                count: filteredRetryableIds.length,
              })}
            </Button>
          </div>
        )}
      </Fragment>
    </Dialog>
  );
};
